import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Check, User, Mail, Phone, Shield, Save, Clock, Calendar, Target } from 'lucide-react';
import { changePassword, updateProfile, getWeeklyDefaults, saveWeeklyDefaults } from '../utils/api';
import { toast } from 'sonner';
import AgentLayout from '../components/AgentLayout';

const AgentSettings = () => {
  const navigate = useNavigate();
  const [agentInfo, setAgentInfo] = useState(null);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileData, setProfileData] = useState({ fullName: '', phone: '' });
  const [profileErrors, setProfileErrors] = useState({});
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [weeklyDefaults, setWeeklyDefaults] = useState(null);
  const [activityDefaults, setActivityDefaults] = useState(null);
  const [goalsDefaults, setGoalsDefaults] = useState(null);
  const [isSavingDefaults, setIsSavingDefaults] = useState(false);

  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const ACTIVITY_FIELDS = [
    { key: 'leadsWorked', label: 'Leads' },
    { key: 'doorKnocks', label: 'Door Knocks' },
    { key: 'phoneCalls', label: 'Phone Calls' },
    { key: 'presentations', label: 'Presentations' },
    { key: 'futureAppointments', label: 'Future Appts' },
  ];
  const GOAL_FIELDS = [
    { key: 'leadsGoal', label: 'Leads' },
    { key: 'doorKnocksGoal', label: 'Door Knocks' },
    { key: 'phoneCallsGoal', label: 'Phone Calls' },
    { key: 'presentationsGoal', label: 'Presentations' },
    { key: 'salesGoal', label: 'Sales' },
    { key: 'futureAppointmentsGoal', label: 'Future Appts' },
    { key: 'annualizedPremiumGoal', label: 'Annual Premium' },
    { key: 'referralsReceivedGoal', label: 'Ref. Received' },
    { key: 'referralsContactedGoal', label: 'Ref. Contacted' },
  ];

  const initActivityDefaults = () => DAY_NAMES.map(() => ({
    offDay: false, startTime: '', endTime: '',
    leadsWorked: 0, doorKnocks: 0, phoneCalls: 0, presentations: 0, futureAppointments: 0,
  }));

  const initGoalDefaults = () => DAY_NAMES.map(() => ({
    offDay: false,
    leadsGoal: 0, doorKnocksGoal: 0, phoneCallsGoal: 0, presentationsGoal: 0,
    salesGoal: 0, futureAppointmentsGoal: 0, annualizedPremiumGoal: 0,
    referralsReceivedGoal: 0, referralsContactedGoal: 0,
  }));

  useEffect(() => {
    const token = localStorage.getItem('agentToken');
    const info = localStorage.getItem('agentInfo');
    if (!token || !info) { navigate('/agent/login'); return; }
    const parsed = JSON.parse(info);
    setAgentInfo(parsed);
    setProfileData({ fullName: parsed.fullName || '', phone: parsed.phone || '' });
    loadDefaults(token);
  }, [navigate]);

  const loadDefaults = async (token) => {
    try {
      const res = await getWeeklyDefaults(token);
      // Use saved defaults if they exist and have 7 entries, otherwise initialize blank
      const actDefs = (res.activityDefaults && res.activityDefaults.length === 7) ? res.activityDefaults : initActivityDefaults();
      const goalDefs = (res.goalsDefaults && res.goalsDefaults.length === 7) ? res.goalsDefaults : initGoalDefaults();
      setActivityDefaults(actDefs);
      setGoalsDefaults(goalDefs);
    } catch (e) {
      setActivityDefaults(initActivityDefaults());
      setGoalsDefaults(initGoalDefaults());
    }
  };

  const updateActivityDefault = (dayIndex, field, value) => {
    setActivityDefaults(prev => {
      const updated = [...prev];
      if (field === 'offDay') {
        updated[dayIndex] = { ...updated[dayIndex], offDay: value };
        if (value) { updated[dayIndex].startTime = ''; updated[dayIndex].endTime = ''; ACTIVITY_FIELDS.forEach(f => { updated[dayIndex][f.key] = 0; }); }
      } else {
        updated[dayIndex] = { ...updated[dayIndex], [field]: value };
      }
      return updated;
    });
  };

  const updateGoalDefault = (dayIndex, field, value) => {
    setGoalsDefaults(prev => {
      const updated = [...prev];
      if (field === 'offDay') {
        updated[dayIndex] = { ...updated[dayIndex], offDay: value };
        if (value) { GOAL_FIELDS.forEach(f => { updated[dayIndex][f.key] = 0; }); }
      } else {
        updated[dayIndex] = { ...updated[dayIndex], [field]: value };
      }
      return updated;
    });
  };

  const handleSaveDefaults = async () => {
    setIsSavingDefaults(true);
    try {
      const token = localStorage.getItem('agentToken');
      await saveWeeklyDefaults(activityDefaults, goalsDefaults, token);
      toast.success('Weekly defaults saved! New weeks will use these settings.');
    } catch (e) {
      toast.error('Failed to save defaults');
    } finally {
      setIsSavingDefaults(false);
    }
  };

  const getStrength = (pw) => {
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 10) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return score;
  };

  // Profile validation on blur
  const validateProfileField = (field, value) => {
    const errs = { ...profileErrors };
    if (field === 'fullName' && value.length < 2) errs.fullName = 'Name must be at least 2 characters';
    else delete errs[field];
    setProfileErrors(errs);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (profileData.fullName.length < 2) {
      setProfileErrors({ fullName: 'Name must be at least 2 characters' });
      return;
    }
    setIsSavingProfile(true);
    try {
      const token = localStorage.getItem('agentToken');
      const result = await updateProfile(profileData, token);
      if (result.success) {
        const updatedInfo = { ...agentInfo, fullName: profileData.fullName, phone: profileData.phone };
        setAgentInfo(updatedInfo);
        localStorage.setItem('agentInfo', JSON.stringify(updatedInfo));
        toast.success('Profile updated successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Password validation on blur
  const validatePasswordField = (field, value) => {
    const errs = { ...passwordErrors };
    if (field === 'currentPassword' && !value) errs.currentPassword = 'Current password is required';
    else if (field === 'newPassword' && value.length < 8) errs.newPassword = 'Password must be at least 8 characters';
    else if (field === 'confirmPassword' && value !== passwordData.newPassword) errs.confirmPassword = 'Passwords do not match';
    else delete errs[field];
    setPasswordErrors(errs);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!passwordData.currentPassword) errs.currentPassword = 'Current password is required';
    if (passwordData.newPassword.length < 8) errs.newPassword = 'Password must be at least 8 characters';
    if (passwordData.newPassword !== passwordData.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    if (Object.keys(errs).length > 0) { setPasswordErrors(errs); return; }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('agentToken');
      await changePassword(passwordData.currentPassword, passwordData.newPassword, token);
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const score = getStrength(passwordData.newPassword);
  const level = score <= 1 ? 'Weak' : score <= 3 ? 'Medium' : 'Strong';
  const barColor = score <= 1 ? 'bg-red-500' : score <= 3 ? 'bg-yellow-500' : 'bg-green-500';
  const textColor = score <= 1 ? 'text-red-600' : score <= 3 ? 'text-yellow-600' : 'text-green-600';
  const barWidth = score <= 1 ? 'w-1/4' : score <= 3 ? 'w-2/4' : 'w-full';

  if (!agentInfo) return null;

  return (
    <AgentLayout title="Settings">
      <div className="max-w-2xl mx-auto space-y-6" data-testid="agent-settings-page">
        {/* Editable Profile Card */}
        <div className="bg-white rounded-lg shadow-md p-6" data-testid="profile-info-card">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
            <User size={20} className="text-brand-blue" /> Profile Information
          </h2>
          <form onSubmit={handleSaveProfile}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="profile-name" className="text-xs font-semibold text-gray-500 block mb-1">Full Name *</label>
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    id="profile-name"
                    type="text"
                    value={profileData.fullName}
                    onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                    onBlur={(e) => validateProfileField('fullName', e.target.value)}
                    className={`w-full pl-9 pr-3 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue ${profileErrors.fullName ? 'border-red-300' : 'border-gray-300'}`}
                    data-testid="profile-name-input"
                  />
                </div>
                {profileErrors.fullName && <p className="text-xs text-red-500 mt-1" role="alert">{profileErrors.fullName}</p>}
              </div>
              <div>
                <label htmlFor="profile-phone" className="text-xs font-semibold text-gray-500 block mb-1">Phone</label>
                <div className="relative">
                  <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    id="profile-phone"
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    className="w-full pl-9 pr-3 py-2.5 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    placeholder="(912) 376-9006"
                    data-testid="profile-phone-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Email</label>
                <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 rounded-md border text-sm">
                  <Mail size={14} className="text-gray-400" />
                  <span data-testid="profile-email">{agentInfo.email}</span>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Role / Team(s)</label>
                <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 rounded-md border text-sm">
                  <Shield size={14} className="text-gray-400" />
                  <span className="capitalize" data-testid="profile-role">{agentInfo.role?.replace('_', ' ')}</span>
                  {agentInfo.teams?.length > 0 && <span className="text-gray-400">|</span>}
                  <span data-testid="profile-teams">{agentInfo.teams?.join(', ')}</span>
                </div>
              </div>
            </div>
            <button
              type="submit"
              disabled={isSavingProfile}
              className="flex items-center gap-2 bg-brand-blue text-white px-5 py-2.5 rounded-md hover:bg-brand-orange transition font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="save-profile-btn"
            >
              <Save size={16} />
              {isSavingProfile ? 'Saving...' : 'Save Profile'}
            </button>
          </form>
        </div>

        {/* Change Password Card */}
        <div className="bg-white rounded-lg shadow-md p-6" data-testid="change-password-card">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
            <Lock size={20} className="text-brand-blue" /> Change Password
          </h2>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label htmlFor="current-pw" className="block text-sm font-medium text-gray-700 mb-1">Current Password *</label>
              <div className="relative">
                <input
                  id="current-pw"
                  type={showCurrent ? 'text' : 'password'}
                  required
                  value={passwordData.currentPassword}
                  onChange={(e) => { setPasswordData({ ...passwordData, currentPassword: e.target.value }); if (passwordErrors.currentPassword) setPasswordErrors({ ...passwordErrors, currentPassword: undefined }); }}
                  onBlur={(e) => validatePasswordField('currentPassword', e.target.value)}
                  className={`w-full px-4 py-3 pr-12 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue ${passwordErrors.currentPassword ? 'border-red-300' : 'border-gray-300'}`}
                  placeholder="Enter current password"
                  data-testid="current-password-input"
                />
                <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1} data-testid="toggle-current-password">
                  {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {passwordErrors.currentPassword && <p className="text-xs text-red-500 mt-1" role="alert">{passwordErrors.currentPassword}</p>}
            </div>

            <div>
              <label htmlFor="new-pw" className="block text-sm font-medium text-gray-700 mb-1">New Password *</label>
              <div className="relative">
                <input
                  id="new-pw"
                  type={showNew ? 'text' : 'password'}
                  required
                  value={passwordData.newPassword}
                  onChange={(e) => { setPasswordData({ ...passwordData, newPassword: e.target.value }); if (passwordErrors.newPassword) setPasswordErrors({ ...passwordErrors, newPassword: undefined }); }}
                  onBlur={(e) => validatePasswordField('newPassword', e.target.value)}
                  className={`w-full px-4 py-3 pr-12 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue ${passwordErrors.newPassword ? 'border-red-300' : 'border-gray-300'}`}
                  placeholder="Minimum 8 characters"
                  data-testid="new-password-input"
                />
                <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1} data-testid="toggle-new-password">
                  {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {passwordErrors.newPassword && <p className="text-xs text-red-500 mt-1" role="alert">{passwordErrors.newPassword}</p>}
              {passwordData.newPassword && (
                <div className="mt-2" data-testid="new-password-strength">
                  <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full ${barColor} ${barWidth} rounded-full transition-all duration-300`}></div>
                  </div>
                  <p className={`text-xs mt-1 font-medium ${textColor}`}>Password strength: {level}</p>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirm-pw" className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password *</label>
              <div className="relative">
                <input
                  id="confirm-pw"
                  type={showConfirm ? 'text' : 'password'}
                  required
                  value={passwordData.confirmPassword}
                  onChange={(e) => { setPasswordData({ ...passwordData, confirmPassword: e.target.value }); if (passwordErrors.confirmPassword) setPasswordErrors({ ...passwordErrors, confirmPassword: undefined }); }}
                  onBlur={(e) => validatePasswordField('confirmPassword', e.target.value)}
                  className={`w-full px-4 py-3 pr-12 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue ${passwordErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'}`}
                  placeholder="Re-enter new password"
                  data-testid="confirm-new-password-input"
                />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1} data-testid="toggle-confirm-password">
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {passwordErrors.confirmPassword && <p className="text-xs text-red-500 mt-1" role="alert">{passwordErrors.confirmPassword}</p>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 bg-brand-blue text-white px-6 py-3 rounded-md hover:bg-brand-orange transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="change-password-submit"
            >
              <Check size={18} />
              {isSubmitting ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>

      {/* Weekly Activity Defaults */}
      <div className="mt-6" data-testid="weekly-activity-defaults-card">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
            <Calendar size={20} className="text-brand-blue" /> Weekly Activity Defaults
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Set your typical weekly schedule and activity targets. These auto-fill when you start a new blank week on the Weekly Activity page.
          </p>
          {activityDefaults && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    <th className="px-1 py-2 text-center font-bold text-xs w-10">Off</th>
                    <th className="px-1 py-2 text-center font-bold text-xs">Day</th>
                    <th className="px-1 py-2 text-center font-bold text-xs">Start Time</th>
                    <th className="px-1 py-2 text-center font-bold text-xs">End Time</th>
                    {ACTIVITY_FIELDS.map(f => (
                      <th key={f.key} className="px-1 py-2 text-center font-bold text-xs">{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activityDefaults.map((day, i) => (
                    <tr key={i} className={`border-b hover:bg-gray-50 ${day.offDay ? 'bg-gray-100 opacity-60' : ''}`} data-testid={`activity-default-row-${i}`}>
                      <td className="px-1 py-1.5 text-center">
                        <input type="checkbox" checked={day.offDay} onChange={(e) => updateActivityDefault(i, 'offDay', e.target.checked)} className="w-4 h-4 cursor-pointer" data-testid={`activity-default-offday-${i}`} />
                      </td>
                      <td className="px-1 py-1.5 text-center font-semibold text-xs whitespace-nowrap">{DAY_NAMES[i]}</td>
                      <td className="px-1 py-1.5 text-center">
                        <input type="time" value={day.startTime} onChange={(e) => updateActivityDefault(i, 'startTime', e.target.value)} disabled={day.offDay} className="w-28 px-2 py-1 border rounded text-center text-sm disabled:bg-gray-100" data-testid={`activity-default-start-${i}`} />
                      </td>
                      <td className="px-1 py-1.5 text-center">
                        <input type="time" value={day.endTime} onChange={(e) => updateActivityDefault(i, 'endTime', e.target.value)} disabled={day.offDay} className="w-28 px-2 py-1 border rounded text-center text-sm disabled:bg-gray-100" data-testid={`activity-default-end-${i}`} />
                      </td>
                      {ACTIVITY_FIELDS.map(f => (
                        <td key={f.key} className="px-1 py-1.5 text-center">
                          <input type="number" value={day[f.key]} onChange={(e) => updateActivityDefault(i, f.key, parseInt(e.target.value) || 0)} disabled={day.offDay} className="w-16 px-1 py-1 border rounded text-center text-sm disabled:bg-gray-100" min="0" data-testid={`activity-default-${f.key}-${i}`} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Weekly Goals Defaults */}
      <div className="mt-6" data-testid="weekly-goals-defaults-card">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
            <Target size={20} className="text-brand-orange" /> Weekly Goals Defaults
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Set your typical weekly goal targets. These auto-fill when you start a new blank week on the Weekly Goals page.
          </p>
          {goalsDefaults && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    <th className="px-1 py-2 text-center font-bold text-xs w-10">Off</th>
                    <th className="px-1 py-2 text-center font-bold text-xs">Day</th>
                    {GOAL_FIELDS.map(f => (
                      <th key={f.key} className="px-1 py-2 text-center font-bold text-xs">{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {goalsDefaults.map((day, i) => (
                    <tr key={i} className={`border-b hover:bg-gray-50 ${day.offDay ? 'bg-gray-100 opacity-60' : ''}`} data-testid={`goal-default-row-${i}`}>
                      <td className="px-1 py-1.5 text-center">
                        <input type="checkbox" checked={day.offDay} onChange={(e) => updateGoalDefault(i, 'offDay', e.target.checked)} className="w-4 h-4 cursor-pointer" data-testid={`goal-default-offday-${i}`} />
                      </td>
                      <td className="px-1 py-1.5 text-center font-semibold text-xs whitespace-nowrap">{DAY_NAMES[i]}</td>
                      {GOAL_FIELDS.map(f => (
                        <td key={f.key} className="px-1 py-1.5 text-center">
                          <input
                            type="number"
                            step={f.key === 'annualizedPremiumGoal' ? '0.01' : '1'}
                            value={day[f.key]}
                            onChange={(e) => updateGoalDefault(i, f.key, f.key === 'annualizedPremiumGoal' ? (parseFloat(e.target.value) || 0) : (parseInt(e.target.value) || 0))}
                            disabled={day.offDay}
                            className={`px-1 py-1 border rounded text-center text-sm disabled:bg-gray-100 ${f.key === 'annualizedPremiumGoal' ? 'w-20' : 'w-16'}`}
                            min="0"
                            data-testid={`goal-default-${f.key}-${i}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Save All Defaults */}
      <div className="mt-4 mb-6">
        <button
          onClick={handleSaveDefaults}
          disabled={isSavingDefaults}
          className="flex items-center gap-2 bg-brand-blue text-white px-6 py-3 rounded-md hover:bg-brand-orange transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="save-defaults-btn"
        >
          <Save size={18} />
          {isSavingDefaults ? 'Saving...' : 'Save All Defaults'}
        </button>
      </div>
    </AgentLayout>
  );
};

export default AgentSettings;
